import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Sparkles, ShieldCheck, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { REGIONS } from "@/lib/regions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const EMPTY = {
  name: "",
  description: "",
  category: "coop",
  region: "all",
  url: "",
  contact: "",
  teaching_philosophy: "",
  florida_expertise: "",
};

export default function SuggestResourceDialog({ triggerVariant = "solid", presetCategory }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, category: presetCategory || EMPTY.category });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const openDialog = () => {
    setForm({ ...EMPTY, category: presetCategory || EMPTY.category });
    setResume(null);
    setOpen(true);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > 5 * 1024 * 1024) { toast.error("Resume must be under 5 MB."); return; }
    setResume(f);
  };

  const submit = async () => {
    if (form.name.trim().length < 2) { toast.error("Give it a name."); return; }
    if (form.description.trim().length < 10) { toast.error("Add a few more words to the description."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("description", form.description.trim());
      fd.append("category", form.category);
      fd.append("region", form.region);
      if (form.url) fd.append("url", form.url.trim());
      if (form.contact) fd.append("contact", form.contact.trim());
      if (form.teaching_philosophy) fd.append("teaching_philosophy", form.teaching_philosophy.trim());
      if (form.florida_expertise) fd.append("florida_expertise", form.florida_expertise.trim());
      if (resume) fd.append("resume", resume);

      await axios.post(`${API}/resource-suggestions`, fd);
      toast.success("Thanks! Your suggestion is queued for review.");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const triggerClass =
    triggerVariant === "muted"
      ? "text-xs text-amber-700 hover:text-amber-900 underline underline-offset-2 font-medium"
      : "rounded-full bg-white border border-stone-200 hover:border-amber-300 hover:bg-amber-50 text-stone-700 hover:text-amber-800 font-medium text-sm px-4 py-2 inline-flex items-center gap-1.5 transition-colors";

  return (
    <>
      <button type="button" onClick={openDialog} className={triggerClass} data-testid="suggest-resource-open">
        <Sparkles className="w-3.5 h-3.5" /> Suggest a resource
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="suggest-resource-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Suggest a resource
            </DialogTitle>
            <DialogDescription>
              Know a Florida homeschool co-op or tutor we should list? Submit it here and our team will review before it goes live.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="suggest-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coop">Co-op</SelectItem>
                    <SelectItem value="tutor">Tutor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Region</Label>
                <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="suggest-region"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((r) => (
                      <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="sr-name">Name</Label>
              <Input id="sr-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sunshine Homeschool Co-op" className="mt-1.5" data-testid="suggest-name" />
            </div>

            <div>
              <Label htmlFor="sr-desc">Description</Label>
              <Textarea id="sr-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What do they offer? Who is it for?" rows={3} className="mt-1.5 resize-none" data-testid="suggest-description" />
              <div className="mt-1 text-[11px] text-stone-400 text-right">{form.description.length}/2000</div>
            </div>

            {/* Professional Bio section */}
            <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-4 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-600">Professional bio</div>
                <div className="text-xs text-stone-500 mt-0.5">Optional — helps families understand who you are. Shown publicly once approved.</div>
              </div>

              <div>
                <Label htmlFor="sr-phil">Teaching philosophy</Label>
                <Textarea id="sr-phil" value={form.teaching_philosophy} onChange={(e) => setForm({ ...form, teaching_philosophy: e.target.value })} placeholder="e.g. Interest-led, Charlotte-Mason inspired. I believe kids learn best when…" rows={3} className="mt-1.5 resize-none" data-testid="suggest-philosophy" />
              </div>

              <div>
                <Label htmlFor="sr-fl">Florida-specific expertise</Label>
                <Textarea id="sr-fl" value={form.florida_expertise} onChange={(e) => setForm({ ...form, florida_expertise: e.target.value })} placeholder="e.g. FES-EO reimbursement guidance, WRAT-5 prep, FL Statute 1002.41 compliance…" rows={3} className="mt-1.5 resize-none" data-testid="suggest-fl-expertise" />
              </div>

              <div>
                <Label>Certifications / resume <span className="text-stone-400 font-normal text-xs">(optional, private — admin-only)</span></Label>
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  <input ref={fileRef} type="file" accept=".pdf,image/*" onChange={onPickFile} className="hidden" data-testid="suggest-resume-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-full" data-testid="suggest-resume-picker">
                    <Paperclip className="w-3.5 h-3.5 mr-1.5" /> {resume ? "Change file" : "Attach PDF or image"}
                  </Button>
                  {resume && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-stone-200 pl-3 pr-1 py-1 text-xs text-stone-700" data-testid="suggest-resume-chip">
                      <Paperclip className="w-3 h-3 text-stone-400" />
                      <span className="truncate max-w-[16rem]">{resume.name}</span>
                      <button type="button" onClick={() => setResume(null)} className="rounded-full hover:bg-stone-100 p-1" aria-label="Remove file">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
                <div className="mt-1.5 text-[11px] text-stone-500 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                  Never shown publicly — only visible on the admin dashboard for vetting.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="sr-url">Website <span className="text-stone-400 font-normal text-xs">(optional)</span></Label>
                <Input id="sr-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" className="mt-1.5" data-testid="suggest-url" />
              </div>
              <div>
                <Label htmlFor="sr-contact">Contact <span className="text-stone-400 font-normal text-xs">(optional)</span></Label>
                <Input id="sr-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Email or phone" className="mt-1.5" data-testid="suggest-contact" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="suggest-cancel">Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="suggest-submit">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-1.5" /> Submit for review</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
